import React, { useState, useRef, useEffect } from "react";
import "./magnify-image.scss";

export interface ImageProps {
    height?: number | string;
    width?: number | string;
    alt?: string;
    customClass?: string;
    customStyle?: React.CSSProperties;
}

export interface MagnifyImageProps {
    imageUrl: string;
    mainImageProps?: ImageProps;
    magnifiedImageProps?: ImageProps & {
        borderColor?: string;
        borderWidth?: number;
        borderRadius?: number;
        boxShadow?: string;
        lensSize?: number;
        position?: "left" | "right";
        customClass?: string;
        customStyle?: React.CSSProperties;
        gap?: number;
    };
    [key: string]: any;
}

export interface ImageSize {
    width: string | number;
    height: string | number;
}

const MagnifyImage: React.FC<MagnifyImageProps> = ({
    imageUrl,
    mainImageProps = {},
    magnifiedImageProps = {},
    ...props
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [imageSize, setImageSize] = useState<ImageSize>({ width: "", height: "" });
    const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
    const [isValidImage, setIsValidImage] = useState(true);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const lensRef = useRef<HTMLDivElement>(null);
    const resultRef = useRef<HTMLDivElement>(null);

    // Default values
    const defaultMainImageProps: ImageProps = {
        alt: "Preview of file",
        customClass: "",
        customStyle: {},
    };

    const defaultMagnifiedImageProps = {
        borderColor: "#ccc",
        height: 300,
        width: 300,
        borderWidth: 1,
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        lensSize: 50,
        position: "right" as const,
        gap: 10,
    };

    // Merge default props with provided props
    const mainImage = { ...defaultMainImageProps, ...mainImageProps };
    const magnifiedImage = { ...defaultMagnifiedImageProps, ...magnifiedImageProps };

    const updateImageSize = () => {
        if (imageRef.current) {
            setIsValidImage(true);
            if (imageUrl === "assets/images/Default_image.png") {
                setIsValidImage(false);
                return;
            }
            const rect = imageRef.current.getBoundingClientRect();
            setImageSize({
                width: mainImage.width ?? rect.width,
                height: mainImage.height ?? rect.height,
            });
        }
    };

    useEffect(() => {
        // Drop the previous measurement so the new image is measured once it has
        // actually loaded (see the img onLoad handler) instead of reusing the
        // size of the image it replaced.
        setImageSize({ width: "", height: "" });
        updateImageSize();
        window.addEventListener("resize", updateImageSize);
        return () => window.removeEventListener("resize", updateImageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl]);

    const getCursorPos = (e: MouseEvent) => {
        if (!imageRef.current) return { x: 0, y: 0 };

        const rect = imageRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        return { x, y };
    };

    const moveLens = (e: MouseEvent) => {
        if (!isValidImage || !imageRef.current || !resultRef.current) return;

        const pos = getCursorPos(e);
        const lensSize = magnifiedImage.lensSize;
        const halfLensSize = lensSize / 2;
        const rect = imageRef.current.getBoundingClientRect();

        // Calculate lens position
        let x = pos.x - halfLensSize;
        let y = pos.y - halfLensSize;

        // Prevent lens from going outside the image
        x = Math.max(0, Math.min(x, rect.width - lensSize));
        y = Math.max(0, Math.min(y, rect.height - lensSize));

        setLensPosition({ x, y });

        // Calculate background position for the magnified image. The panel is
        // still hidden on the first mouse move of a hover, so fall back to the
        // configured size while its client box measures zero.
        const resultWidth = resultRef.current.clientWidth || Number(magnifiedImage.width) || 0;
        const resultHeight = resultRef.current.clientHeight || Number(magnifiedImage.height) || 0;
        const scaleX = resultWidth / lensSize;
        const scaleY = resultHeight / lensSize;
        const bgX = x * scaleX;
        const bgY = y * scaleY;

        if (resultRef.current) {
            resultRef.current.style.backgroundPosition = `-${bgX}px -${bgY}px`;
            resultRef.current.style.backgroundSize = `${rect.width * scaleX}px ${rect.height * scaleY}px`;
        }
    };

    const moveLensRef = useRef(moveLens);
    useEffect(() => {
        moveLensRef.current = moveLens;
    });

    // Kendo windows (WmsModel) are appended to #root, which moves this subtree
    // out of the React root container it was rendered into, and React only
    // delegates events from its own root container. Native listeners keep the
    // magnifier working wherever the markup ends up in the DOM.
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const handleMouseEnter = () => setIsHovered(true);
        const handleMouseLeave = () => setIsHovered(false);
        const handleMouseMove = (event: MouseEvent) => moveLensRef.current(event);

        wrapper.addEventListener("mouseenter", handleMouseEnter);
        wrapper.addEventListener("mouseleave", handleMouseLeave);
        wrapper.addEventListener("mousemove", handleMouseMove);

        return () => {
            wrapper.removeEventListener("mouseenter", handleMouseEnter);
            wrapper.removeEventListener("mouseleave", handleMouseLeave);
            wrapper.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    // The panel is absolutely positioned inside the wrapper, so it is offset
    // from the image box itself and stays correct wherever the wrapper is laid
    // out (modal, scrolled container, etc.).
    const getMagnifiedStyle = (): React.CSSProperties => {
        const gapOffset = `calc(100% + ${magnifiedImage.gap}px)`;
        return {
            top: 0,
            ...(magnifiedImage.position === "right" ? { left: gapOffset } : { right: gapOffset }),
            backgroundImage: `url(${imageUrl})`,
            width: magnifiedImage.width ?? imageSize.width,
            height: magnifiedImage.height ?? imageSize.height,
            border: `${magnifiedImage.borderWidth}px solid ${magnifiedImage.borderColor}`,
            borderRadius: magnifiedImage.borderRadius ? `${magnifiedImage.borderRadius}px` : undefined,
            boxShadow: magnifiedImage.boxShadow,
            display: isHovered && isValidImage ? "block" : "none",
            ...magnifiedImage.customStyle,
        };
    };

    return (
        <div ref={wrapperRef} className="magnify-image-wrapper" {...props}>
            <img
                ref={imageRef}
                src={imageUrl}
                alt={mainImage.alt}
                height={imageSize.height}
                width={imageSize.width}
                onLoad={updateImageSize}
                className={`main-image ${mainImage.customClass ?? ""}`}
                style={{
                    ...mainImage.customStyle,
                }}
            />
            {isHovered && isValidImage && (
                <div
                    ref={lensRef}
                    className="img-zoom-lens"
                    style={{
                        width: magnifiedImage.lensSize,
                        height: magnifiedImage.lensSize,
                        left: lensPosition.x,
                        top: lensPosition.y,
                        border: `${magnifiedImage.borderWidth}px solid ${magnifiedImage.borderColor}`,
                        borderRadius: magnifiedImage.borderRadius ? `${magnifiedImage.borderRadius}px` : undefined,
                    }}
                />
            )}
            <div
                ref={resultRef}
                className={`magnified-image ${magnifiedImage.customClass ?? ""}`}
                style={getMagnifiedStyle()}
            />
        </div>
    );
};

export default MagnifyImage;
